/**
 * Shared decorative values for the /about-us visual design, kept in one place so the
 * hero tiles and the nav's active tab can never drift apart.
 *
 * Colour policy. The LIGHT half of the page maps cleanly onto repo tokens, and does not appear
 * here: gray-1/2/3 for name/bio/role, gray-5 for the roster rules, light-background-100 / white
 * / #eef3f5 for the three section tones.
 *
 * The DARK half carries raw hex on purpose. These are Figma variables with no repo equivalent —
 * `color/Brand Teal` #2ca7be, `color/Brand Purple` #9b5de5, `color/border/default` #262626,
 * `color/text/primary` #fafafa, `color/text/inverse` #171717 — and kraidle's `--kr-*` custom
 * properties are scoped under `.platform-theme`, which this page is not wrapped in, so they
 * aren't reachable. Every value below is measured from the design rather than eyeballed; the
 * nearest repo token was tried first and was visibly wrong in each case.
 */

/**
 * Height of the sticky anchor bar. Lives here rather than in AboutNav because AboutNav is a
 * client module: importing a value from it into the server-rendered PeopleSection yields a
 * client-reference stub, not the number, which silently corrupts the scroll-margin calc.
 * PeopleSection adds this to its scroll-margin so a tab click lands the section heading
 * below the bar; AboutNav's own `min-h-[74px]` must stay in step with it.
 */
export const ABOUT_NAV_HEIGHT = 74;

/** Tinted grid cells — teal → violet on the diagonal. Figma `color/Brand Teal` → `Brand Purple`. */
export const TILE_GRADIENT = 'linear-gradient(137.8deg, #2ca7be 0%, #9b5de5 100%)';

/** Active anchor-nav tab fill — teal → blue → violet. */
export const ACTIVE_TAB_GRADIENT = 'linear-gradient(123.88deg, #2ca7be 0%, #528ecb 53.14%, #9b5de5 155.71%)';

/** The hero's flat ground. */
export const HERO_BG = '#0a0c0d';

/**
 * Measure of the hero paragraph — an INTENTIONAL DEVIATION from the Figma frame, at the author's
 * request: the frame's paragraph runs ~1155px and wraps to five lines, this wraps to six, which
 * reads better. Do not "correct" this back to match the frame.
 *
 * Measured, not estimated. At 1440 the six-line band is 965–1150px (≥1155 gives five lines, ≤960
 * gives seven); 1060 is that band's midpoint, leaving ~95px of clearance either side so font
 * loading or platform rasterisation can't tip the line count. Below roughly a 1172px viewport
 * this stops binding and px-section governs, so narrower breakpoints are untouched.
 */
export const HERO_PARAGRAPH_MAX_WIDTH = 1060;

/**
 * Grid geometry, measured from Figma node 17013:1939 rather than eyeballed: the grid is two
 * 1144px frames of individual rects, 12 columns each, so one cell is exactly 1144/12.
 */
const DESIGN_WIDTH = 1912;
export const GRID_CELL = 1144 / 12; // 95.3333…, NOT 95

/**
 * Both the grid and the tinted cells are positioned from the same origin: the left edge of the
 * design's 1912px band, centred in the viewport. That keeps them in phase with each other at
 * every width (a left-anchored grid plus centre-anchored cells would drift), and it holds the
 * design's arrangement relative to the centred copy instead of letting cells slide inward as
 * the viewport narrows. Wider viewports extend the grid outward; narrower ones crop it
 * symmetrically. Fixed pitch, per the site's other grid backgrounds — see HERO_GRID_STYLE.
 */
export const gridOriginX = (offsetPx: number) => `calc(50% - ${DESIGN_WIDTH / 2}px + ${offsetPx.toFixed(3)}px)`;

/**
 * The graph-paper grid. In Figma this is ~190 individually-drawn 95.33×95.33 rectangles, each
 * with no fill and a 0.433px `#a3a3a3` stroke; as CSS that is one repeating cell whose 1px lines
 * carry the stroke's coverage as alpha (0.433 of #a3a3a3). Adjacent rectangles share their edges
 * and the stroke is opaque, so interior lines do NOT double up.
 *
 * No mask here: the vignette below is the design's own top fade and does that job.
 */
export const HERO_GRID_STYLE = {
  backgroundImage:
    'linear-gradient(to right, rgba(163,163,163,0.43) 1px, transparent 1px), linear-gradient(to bottom, rgba(163,163,163,0.43) 1px, transparent 1px)',
  backgroundSize: `${GRID_CELL}px ${GRID_CELL}px`,
  // Same origin as the tinted cells, so grid lines and cells stay in phase at every width.
  backgroundPosition: `${gridOriginX(0)} top`,
} as const;

const HERO_VIGNETTE_HEIGHT = 752;

/**
 * The design's vignette. It paints over the grid and cells but under the copy, so it mutes the
 * cells — heavily at the top of the band, less further down — without touching text contrast.
 *
 * The catch: the gradient VECTOR is longer than the rect it paints. Figma's handle sits at
 * 1.654× the rect height (752 × 1.654 = 1244px), with the opaque stop at 3.7% of that vector
 * (46px). Taking the rect height as the gradient length instead — the obvious-looking reading —
 * fades far too fast and leaves the cells visibly brighter than the design (45% vs the correct
 * 68% coverage over the lowest cell). Absolute px stops, so rect and gradient can't drift apart.
 */
export const HERO_VIGNETTE_STYLE = {
  background: `linear-gradient(180deg, ${HERO_BG} 46px, rgba(10,12,13,0) 1244px)`,
  height: `${HERO_VIGNETTE_HEIGHT}px`,
} as const;

/**
 * The six tinted cells visible inside the design's 1912×572 hero, as AUTHORED grid coordinates
 * measured from Figma — never generated or randomised, so a screenshot is deterministic. The
 * other four cells in the design fall outside the visible band and are omitted.
 *
 * Positioned from the shared centred origin (see {@link gridOriginX}), which is what keeps the
 * design's arrangement relative to the centred copy at every width. An earlier version anchored
 * the right-hand three to the right edge, which made them slide inward as the viewport narrowed
 * — that was the "differently arranged" mismatch.
 */
export const HERO_TILES = [
  { col: 7, row: 0 },
  { col: 15, row: 0 },
  { col: 4, row: 1 },
  { col: 16, row: 1 },
  { col: 1, row: 2 },
  { col: 18, row: 4 },
] as const;
